import type { ComponentProps, ReactNode } from "react";
import { CommandInput, CommandItem } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { SelectorAvatar, SelectorAvatarStack } from "./selector-avatar";
import type { SelectorAccount, SelectorGroup, SelectorSize } from "./types";

export type SelectionState = false | true | "indeterminate";

export function SelectorSearchInput(
  props: ComponentProps<typeof CommandInput>
) {
  return (
    <CommandInput
      leadingIcon={
        <i
          aria-hidden
          className="fa-regular fa-magnifying-glass size-5 shrink-0 text-center text-[16px] leading-5 text-neutral-500"
        />
      }
      {...props}
    />
  );
}

export function selectionStateLabel(checked: SelectionState) {
  if (checked === "indeterminate") return "partially selected";
  return checked ? "selected" : "not selected";
}

export function MenuCheckbox({ checked }: { checked: SelectionState }) {
  return (
    <span
      aria-hidden
      data-state={
        checked === "indeterminate"
          ? "indeterminate"
          : checked
            ? "checked"
            : "unchecked"
      }
      className={cn(
        "flex size-4 shrink-0 items-center justify-center rounded-[4px] border shadow-xs",
        checked === false && "border-neutral-200 bg-white",
        checked === true && "border-blue-500 bg-blue-500 text-white",
        checked === "indeterminate" &&
          "border-blue-500 bg-blue-50 text-blue-500"
      )}
    >
      {checked === "indeterminate" ? (
        <i className="fa-solid fa-minus text-[10px] leading-none text-blue-500" />
      ) : checked ? (
        <i className="fa-solid fa-check text-[10px] leading-none text-white" />
      ) : null}
    </span>
  );
}

export function AccountMenuItem({
  account,
  selected,
  multi = false,
  nested = false,
  size = "md",
  onSelect,
}: {
  account: SelectorAccount;
  selected: boolean;
  multi?: boolean;
  nested?: boolean;
  size?: SelectorSize;
  onSelect: () => void;
}) {
  const attentionReason = account.disabledReason || account.attentionReason;
  const accessibleName = [
    account.label,
    account.secondaryText,
    !multi && selected ? "Selected" : undefined,
    attentionReason,
  ]
    .filter(Boolean)
    .join(". ");
  const content = (
    <>
      {multi ? <MenuCheckbox checked={selected} /> : null}
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <SelectorAvatar account={account} size={20} />
        <span className="min-w-0 flex-1">
          <span className="block truncate">{account.label}</span>
          {account.secondaryText ? (
            <span className="block truncate text-xs text-neutral-500">
              {account.secondaryText}
            </span>
          ) : null}
        </span>
      </span>
      {!multi && selected ? (
        <>
          <span className="sr-only">Selected</span>
          <i
            aria-hidden
            className="fa-regular fa-check ml-auto size-5 text-center text-[16px] leading-5 text-blue-500"
          />
        </>
      ) : null}
    </>
  );
  const className = cn(
    "relative flex w-full cursor-pointer items-center rounded-none font-inter text-sm font-medium text-neutral-800 outline-hidden select-none",
    size === "md" ? "h-10 px-3.5 py-2" : "h-8 px-3 py-1.5",
    nested && (size === "md" ? "pr-3.5 pl-[42px]" : "pr-3 pl-[38px]"),
    multi &&
      "gap-2 text-left hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:text-neutral-900 disabled:pointer-events-none disabled:opacity-50"
  );

  if (multi) {
    return (
      <button
        type="button"
        role="menuitemcheckbox"
        aria-checked={selected}
        aria-label={attentionReason ? accessibleName : account.label}
        disabled={account.disabled}
        title={attentionReason}
        data-testid={account.testId}
        className={className}
        onClick={onSelect}
      >
        {content}
      </button>
    );
  }

  return (
    <CommandItem
      value={`${account.label} ${account.secondaryText || ""} ${account.searchText || ""} ${account.id}`}
      aria-label={attentionReason ? accessibleName : undefined}
      disabled={account.disabled}
      onSelect={onSelect}
      title={attentionReason}
      data-testid={account.testId}
      className={className}
    >
      {content}
    </CommandItem>
  );
}

export function GroupMenuItem({
  group,
  selected,
  checkbox,
  size = "md",
  onSelect,
  children,
}: {
  group: SelectorGroup;
  selected?: boolean;
  checkbox?: SelectionState;
  size?: SelectorSize;
  onSelect: () => void;
  children?: ReactNode;
}) {
  return (
    <CommandItem
      value={`${group.label} ${group.id}`}
      disabled={group.disabled}
      onSelect={onSelect}
      title={group.disabledReason}
      aria-label={
        checkbox !== undefined
          ? `${group.label}, ${selectionStateLabel(checkbox)}`
          : undefined
      }
      className={cn(
        "w-full cursor-pointer rounded-none font-inter text-sm font-medium text-neutral-800",
        size === "md" ? "h-10 px-3.5 py-2.5" : "h-8 px-3 py-1.5"
      )}
    >
      {checkbox !== undefined ? <MenuCheckbox checked={checkbox} /> : null}
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span className="min-w-0 truncate">{group.label}</span>
        <SelectorAvatarStack
          accounts={group.accounts || []}
          max={4}
          size={20}
        />
      </span>
      {checkbox === undefined && selected ? (
        <>
          <span className="sr-only">Selected</span>
          <i
            aria-hidden
            className="fa-regular fa-check ml-auto size-5 text-center text-[16px] leading-5 text-blue-500"
          />
        </>
      ) : null}
      {children}
    </CommandItem>
  );
}
